import { DateTime } from 'luxon';

/**
 * Convert 24-hour time to 12-hour format with AM/PM
 * @param time24 - Time in 24-hour format (e.g., "14:00")
 * @returns Time in 12-hour format (e.g., "2:00 PM")
 */
export const convert24To12Hour = (time24: string): string => {
  const [hours24, minutes] = time24.split(':').map(Number);
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

/**
 * Convert 12-hour time with AM/PM to 24-hour format
 * @param time12 - Time in 12-hour format (e.g., "2:00 PM")
 * @returns Time in 24-hour format (e.g., "14:00")
 */
export const convert12To24Hour = (time12: string): string => {
  const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return time12; // Return as-is if format doesn't match

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();

  if (period === 'AM') {
    hours = hours === 12 ? 0 : hours;
  } else {
    hours = hours === 12 ? 12 : hours + 12;
  }

  return `${String(hours).padStart(2, '0')}:${minutes}`;
};

/**
 * Get the current time in a specific timezone
 * @param timezone - IANA timezone string (e.g., 'America/Los_Angeles')
 * @returns DateTime object in the specified timezone
 */
export const getNowInTimezone = (timezone: string): DateTime => {
  return DateTime.now().setZone(timezone);
};

/**
 * Get the current date (start of day) in a specific timezone
 * @param timezone - IANA timezone string (e.g., 'America/Los_Angeles')
 * @returns Date object representing today at 00:00 in the specified timezone
 */
export const getTodayInTimezone = (timezone: string): Date => {
  const nowInTimezone = DateTime.now().setZone(timezone);
  return nowInTimezone.startOf('day').toJSDate();
};

/**
 * Get the current hour in a specific timezone
 * @param timezone - IANA timezone string (e.g., 'America/Los_Angeles')
 * @returns Current hour (0-23) in the specified timezone
 */
export const getCurrentHourInTimezone = (timezone: string): number => {
  return DateTime.now().setZone(timezone).hour;
};

/**
 * Check if a date is today in a specific timezone
 * @param date - Date to check
 * @param timezone - IANA timezone string (e.g., 'America/Los_Angeles')
 * @returns true if the date is today in the specified timezone
 */
export const isTodayInTimezone = (date: Date, timezone: string): boolean => {
  const nowInTimezone = DateTime.now().setZone(timezone);
  const todayInTimezone = nowInTimezone.startOf('day');

  const dateInTimezone = DateTime.fromJSDate(date).setZone(timezone);
  const dateOnlyInTimezone = dateInTimezone.startOf('day');

  return dateOnlyInTimezone.equals(todayInTimezone);
};

/**
 * Format an instant (ISO string from GraphQL or millis) in the listing vehicle timezone.
 */
export function formatInstantInCarTimezone(
  value: string | number | null | undefined,
  timezone?: string | null,
): string {
  if (value == null || value === '') return '—';
  const zone = timezone && String(timezone).length > 0 ? String(timezone) : 'UTC';
  const dt =
    typeof value === 'number' ? DateTime.fromMillis(value) : DateTime.fromISO(String(value));
  if (!dt.isValid) return '—';
  return dt.setZone(zone).toFormat('MMM d, yyyy, h:mm a ZZZZ');
}

/**
 * Get time options for a date picker, filtered for future times if date is today
 * Returns times in 12-hour format with AM/PM
 * @param selectedDate - The currently selected date
 * @param timezone - IANA timezone string (e.g., 'America/Los_Angeles')
 * @returns Filtered time options in 12-hour format (e.g., ['12:00 AM', '1:00 AM', ...])
 */
export const getFilteredTimeOptions = (selectedDate: Date | null, timezone: string): string[] => {
  // Generate all 24-hour options first
  const allOptions24Hour = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

  let filtered24Hour = allOptions24Hour;

  if (selectedDate) {
    const isToday = isTodayInTimezone(selectedDate, timezone);

    if (isToday) {
      // If today, filter to only show times after current hour
      const currentHour = getCurrentHourInTimezone(timezone);
      const nextHour = currentHour + 1;

      filtered24Hour = allOptions24Hour.filter(time => {
        const hour = parseInt(time.split(':')[0], 10);
        return hour >= nextHour;
      });
    }
  }

  // Convert all options to 12-hour format
  return filtered24Hour.map(time => convert24To12Hour(time));
};

/**
 * Get the next available hour in a specific timezone
 * Returns in 12-hour format with AM/PM
 * @param timezone - IANA timezone string (e.g., 'America/Los_Angeles')
 * @returns Time string in 12-hour format (e.g., "2:00 PM")
 */
export const getNextHourInTimezone = (timezone: string): string => {
  const nowInTimezone = DateTime.now().setZone(timezone);
  const nextHour = nowInTimezone.hour + 1;
  const time24 = `${String(nextHour).padStart(2, '0')}:00`;
  return convert24To12Hour(time24);
};

/**
 * Format an epoch-ms timestamp in a specific IANA timezone using a Luxon
 * format string.  Use this instead of `date-fns format(new Date(ms), ...)`
 * when the display must reflect the car/listing timezone.
 */
export const formatInstantInZone = (epochMs: number, zone: string, pattern: string): string => {
  return DateTime.fromMillis(epochMs).setZone(zone).toFormat(pattern);
};

/**
 * Listing-local clock plus short zone label, e.g. "09/20/2026, 8:00 PM EDT".
 * Uses the instant's own offset (EDT vs EST), not the browser timezone.
 */
export const formatInstantInZoneWithAbbr = (
  epochMs: number,
  zone: string,
  pattern = 'MM/dd/yyyy, h:mm a',
): string => {
  const dt = DateTime.fromMillis(epochMs).setZone(zone);
  if (!dt.isValid) return '—';
  return `${dt.toFormat(pattern)} ${zoneAbbreviationAt(epochMs, zone)}`;
};

/**
 * Calendar Date (local Y/M/D at noon) for the listing-timezone day of [epochMs].
 * Noon avoids browser-midnight shifting the calendar day when the picker later
 * re-interprets the Date in a different zone (PT user + ET listing).
 */
export const calendarJsDateFromMillisInZone = (epochMs: number, timezone: string): Date => {
  const zoned = DateTime.fromMillis(epochMs).setZone(timezone);
  return new Date(zoned.year, zoned.month - 1, zoned.day, 12, 0, 0, 0);
};

/** 24-hour clock time of [epochMs] in the listing timezone (`HH:mm`). */
export const clockTimeInZone = (epochMs: number, timezone: string): string => {
  return DateTime.fromMillis(epochMs).setZone(timezone).toFormat('HH:mm');
};

/** Short zone label for picker copy, e.g. "EDT" / "PDT". */
export const zoneAbbreviation = (timezone: string): string => {
  return zoneAbbreviationAt(Date.now(), timezone);
};

/** Short zone name at [epochMs] in [timezone], e.g. "EDT" not "America/New_York". */
export const zoneAbbreviationAt = (epochMs: number, timezone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(new Date(epochMs));
    const name = parts.find(part => part.type === 'timeZoneName')?.value?.trim();
    if (name && !name.includes('/')) return name;
  } catch {
    // fall through
  }
  const dt = DateTime.fromMillis(epochMs).setZone(timezone);
  const short = dt.offsetNameShort;
  if (short && !short.includes('/')) return short;
  return dt.zoneName || timezone;
};
